import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { path, pipe } from "ramda";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { getProjectLogoUri } from "../../common/selectors";
import { getProject } from "../applicationReducer";
import backgroundImage from "./bg-BiblioBoard-Home.png";
import { Trans, useTranslation } from "react-i18next";

const PATRON_UI_URL = process.env.REACT_APP_PATRON_UI_URL;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: "cover",
    height: "100%",
    width: "100%",
  },

  fullScreen: {
    background: `#0D5876E6 0% 0% no-repeat padding-box`,
    opacity: 1,
    height: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  centerBox: {
    backgroundColor: "#FFF",
    border: "1px solid #CDCDCD",
    borderRadius: 5,
    width: 450,
    height: 600,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    alignItems: "center",
    padding: theme.spacing(5),
  },

  sectionHeader: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  content: {
    fontSize: "1.125rem",
    color: theme.palette.primary.dark,
    textAlign: "center",
  },

  projectName: {
    fontWeight: "bolder",
  },

  actionArea: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
  },

  buttonLnk: {
    borderRadius: 20,
    textTransform: "uppercase",
    backgroundColor: theme.palette.secondary.dark,
  },

  link: {
    marginTop: theme.spacing(2),
  },

  linkText: {
    fontSize: "1rem",
    color: theme.palette.secondary.dark,
  },

  imageContainer: {
    maxHeight: 125,
    maxWidth: 250,
  },

  image: {
    maxHeight: 125,
    maxWidth: 250,
  },

  visuallyHidden: {
    position: "absolute",
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    border: 0,
    whiteSpace: "nowrap" /* Prevents text from wrapping */,
  },
}));

const InactiveProject = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const projectLogoUri = useSelector(getProjectLogoUri);
  const project = useSelector(pipe(path(["application"]), getProject));

  const [projectUrl, setProjectUrl] = useState(null);
  useEffect(() => {
    if (project.redirectUri) {
      if (project.redirectUri.includes("/thank-you")) {
        const url = project.redirectUri.substring(
          0,
          project.redirectUri.lastIndexOf("/")
        );
        setProjectUrl(url);
      } else {
        setProjectUrl(project.redirectUri);
      }
    }
  }, [project]);

  return (
    <Paper classes={{ root: classes.root }} component="main" elevation={0}>
      <div className={classes.fullScreen}>
        <div className={classes.centerBox}>
          <div className={classes.imageContainer}>
            <img alt="Logo" src={projectLogoUri} className={classes.image} />
            {/* Need H1 tag for screen readers */}
            <h1 className={classes.visuallyHidden}>
              {`${
                project?.projectName ?? "Custom Submission Page for BiblioBoard"
              }, an inactive project`}
            </h1>
          </div>
          <p className={classes.content}>
            <Trans i18nKey="inactive_message">
              <span className={classes.projectName}></span>
              {{ projectName: project.projectName }}
            </Trans>
          </p>
          <div className={classes.actionArea}>
            <Button
              classes={{ root: classes.buttonLnk }}
              variant="contained"
              color="secondary"
              href={PATRON_UI_URL}
            >
              {t("inactive_visit_biblioboard")}
            </Button>
            {projectUrl && (
              <Button
                classes={{ text: classes.linkText, root: classes.link }}
                variant="text"
                color="secondary"
                href={projectUrl}
                endIcon={<ArrowForwardIcon />}
              >
                {t("inactive_visit_overview")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Paper>
  );
};

export default InactiveProject;
